import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useAuthSession } from 'hooks/useAuthSession';
import { blocksDashboardAccess } from 'utils/authSession';
import { appendActiveNotificationAction } from 'store/actions/documentActions';
import { getNotifFromAction, getNotifToAction } from 'store/actions/notifActions';
import { isNotificationWebSocketEnabled, notificationWebSocket } from 'utils/notificationWebSocket';

const isNotificationRoute = (pathname) => pathname.startsWith('/notification');

/**
 * Keeps one notification WebSocket per authenticated dashboard session.
 * Mounted once at App root — survives DashboardLayout remounts on route changes.
 *
 * - `message` → bell badge only (no list API calls)
 * - `update` → refresh notification lists only while user is on /notification/*
 */
export function NotificationWebSocketBridge() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { token, user, isSessionComplete } = useAuthSession();
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    if (!isNotificationWebSocketEnabled()) return undefined;

    const shouldConnect =
      isSessionComplete && Boolean(token) && Boolean(user) && !blocksDashboardAccess(user);

    if (!shouldConnect) {
      notificationWebSocket.disconnect();
      return undefined;
    }

    notificationWebSocket.connect(user);

    const unsubscribe = notificationWebSocket.subscribe((kind, payload) => {
      if (kind === 'message' && payload && typeof payload === 'object') {
        dispatch(appendActiveNotificationAction(payload));
        return;
      }
      if (kind !== 'update') return;
      if (!isNotificationRoute(locationRef.current.pathname)) return;
      dispatch(getNotifToAction());
      dispatch(getNotifFromAction());
    });

    const handleBeforeUnload = () => {
      notificationWebSocket.disconnect();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      unsubscribe();
    };
  }, [
    dispatch,
    isSessionComplete,
    token,
    user?.id,
    user?.require2FA,
    user?.requirePasswordUpdate,
    user?.role,
  ]);

  return null;
}
